// apps/client/src/assets/organic.ts (trees and rocks, 2026-10-16) — soft toy organics for the `tree` and `rock` code builders. Every
// shape is low-poly and indexed (shared corners), its normals authored smooth (a canopy's puffs bend toward the canopy's own centre, so
// a cloud of puffs shades like one soft ball), and its colour lives in the vertices: one matte material for every tree and rock, so each
// kind is a single instanced draw (two with its far model, never both for one tree).
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';

/** The toy finish (DESIGN.md: matte, roughness .85 to .95, metalness 0) with the colour in the vertices. */
export const NATURE_MATERIAL = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0 });

const spheres = new Map<number, THREE.BufferGeometry>();
/** A unit icosphere with shared corners (indexed), position only. Detail 1 is 80 triangles, detail 0 is 20. */
export function icosphere(detail: number): THREE.BufferGeometry {
  let g = spheres.get(detail);
  if (!g) {
    const src = new THREE.IcosahedronGeometry(1, detail);
    src.deleteAttribute('normal');
    src.deleteAttribute('uv');
    g = mergeVertices(src, 1e-4);
    src.dispose();
    spheres.set(detail, g);
  }
  return g.clone();
}

/** Colours every vertex of `g` (which has position and normal) from `paint(position, normal, out)`. */
export function paintVertices(g: THREE.BufferGeometry, paint: (p: THREE.Vector3, n: THREE.Vector3, out: THREE.Color) => void): THREE.BufferGeometry {
  const pos = g.getAttribute('position'), nor = g.getAttribute('normal'), col = new Float32Array(pos.count * 3);
  const p = new THREE.Vector3(), n = new THREE.Vector3(), c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i); n.fromBufferAttribute(nor, i);
    paint(p, n, c);
    col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

export interface PuffSpec {
  at: THREE.Vector3;
  r: number;
  /** Per-axis stretch of the ball (1, 1, 1 is round). */
  squash?: readonly [number, number, number];
  /** The canopy's centre: the normals lean toward pointing away from it by `bend` (0 = the ball's own normals). */
  centre?: THREE.Vector3;
  bend?: number;
}
/** One puff of a canopy (or a pebble): a low-poly ball, indexed, with authored smooth normals (no colour yet). */
export function puff(s: PuffSpec, detail: number): THREE.BufferGeometry {
  const g = icosphere(detail), pos = g.getAttribute('position') as THREE.BufferAttribute, n = new Float32Array(pos.count * 3);
  const [sx, sy, sz] = s.squash ?? [1, 1, 1], u = new THREE.Vector3(), own = new THREE.Vector3(), away = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    u.fromBufferAttribute(pos, i);
    own.set(u.x / sx, u.y / sy, u.z / sz).normalize();             // an ellipsoid's normal
    const p = u.set(s.at.x + u.x * sx * s.r, s.at.y + u.y * sy * s.r, s.at.z + u.z * sz * s.r);
    pos.setXYZ(i, p.x, p.y, p.z);
    if (s.centre && s.bend) { away.subVectors(p, s.centre).normalize(); own.lerp(away, s.bend).normalize(); }
    n[i * 3] = own.x; n[i * 3 + 1] = own.y; n[i * 3 + 2] = own.z;
  }
  g.setAttribute('normal', new THREE.BufferAttribute(n, 3));
  return g;
}

/** A turned shape round the y axis from a profile of (radius, height) points, bottom to top: indexed, smooth normals, no uv. */
export function lathe(profile: readonly (readonly [number, number])[], segments: number, phase = 0): THREE.BufferGeometry {
  const src = new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), segments, phase);
  src.deleteAttribute('uv');
  src.deleteAttribute('normal');
  // LatheGeometry repeats the seam's column and every point on the axis; merged, the seam and the tip shade like the rest.
  const g = dropDegenerate(mergeVertices(src, 1e-5));
  src.dispose();
  g.computeVertexNormals();
  return g;
}

/** Drops the triangles that merging collapsed (two corners at one vertex): they draw nothing. */
function dropDegenerate(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const idx = g.index!, keep: number[] = [];
  for (let i = 0; i < idx.count; i += 3) {
    const a = idx.getX(i), b = idx.getX(i + 1), c = idx.getX(i + 2);
    if (a !== b && b !== c && a !== c) keep.push(a, b, c);
  }
  g.setIndex(keep);
  return g;
}

/** Merges coloured indexed parts into one geometry (position, normal, color). */
export function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) throw new Error('organic: parts with different attributes');
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

/** The triangles in a geometry. */
export const triangles = (g: THREE.BufferGeometry): number => (g.index ? g.index.count : g.getAttribute('position').count) / 3;

export const smoothstep = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** Where a ray from `from` along unit `dir` last leaves a union of balls (the far side of the farthest one it passes through), or null. */
export function exitPoint(from: THREE.Vector3, dir: THREE.Vector3, balls: readonly { at: THREE.Vector3; r: number }[]): number | null {
  let best: number | null = null;
  const oc = new THREE.Vector3();
  for (const b of balls) {
    oc.subVectors(from, b.at);
    const half = oc.dot(dir), c = oc.lengthSq() - b.r * b.r, disc = half * half - c;
    if (disc < 0) continue;
    const t = -half + Math.sqrt(disc);
    if (t > 0 && (best === null || t > best)) best = t;
  }
  return best;
}
